// src/components/layout/Sidebar.jsx
import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  // User Workspace
  HomeIcon,
  UserGroupIcon,
  ChartBarIcon,
  // Admin Workspace
  BookOpenIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  IdentificationIcon,
  // Settings section & children
  Cog6ToothIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  CalendarIcon,
  // Administration section & children
  ShieldCheckIcon,
  UserIcon,
  BookmarkSquareIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  // NEW for reports
  UsersIcon,
  // Chrome
  XMarkIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/* ---------------------------------------------
 * Navigation data
 * -------------------------------------------*/
const userWorkspace = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Students', href: '/students', icon: UserGroupIcon },
];

const adminWorkspace = [
  { name: 'Enrollments', href: '/enrollments', icon: BookOpenIcon },
  { name: 'Payments', href: '/payments', icon: CreditCardIcon },
  { name: 'Exam Registrations', href: '/exam-registrations', icon: ClipboardDocumentListIcon },
];

const reports = [
  { name: 'Intake Report', href: '/report-intake', icon: UsersIcon },
  { name: 'Enrollment Report', href: '/report-missing-subjects', icon: UsersIcon }, 
  { name: 'Exam Registration Report', href: '/report-exam-registration', icon: ClipboardDocumentCheckIcon },
  { name: 'RISM Membership Report', href: '/report-rism-membership', icon: UsersIcon },
];

// Settings is a non-routeable header; only children are links
const settingsItems = [
  { name: 'Intakes', href: '/intakes', icon: CalendarDaysIcon },
  { name: 'Subjects', href: '/subjects', icon: AcademicCapIcon },
  { name: 'Exam Sessions', href: '/exam-sessions', icon: CalendarIcon },
];

const administrationItems = [
  { name: 'Students Admin', href: '/admin/students', icon: UserIcon },
  { name: 'Enrollments Admin', href: '/admin/enrollments', icon: BookmarkSquareIcon },
  { name: 'Payments Admin', href: '/admin/payments-archive', icon: BanknotesIcon },
  { name: 'Exam Registrations Admin', href: '/admin/exam-registrations', icon: ClipboardDocumentCheckIcon },
];

/* ---------------------------------------------
 * Row (single link) – themed
 * -------------------------------------------*/
function LinkRow({ item, active, collapsed, onClick }) {
  const Icon = item.icon;

  const base =
    collapsed
      ? 'w-12 h-12 mx-auto flex items-center justify-center rounded-xl'
      : 'group flex items-center px-2 py-2 text-sm font-medium rounded-md';

  const state =
    active
      ? (collapsed
          ? 'bg-blue-100 text-blue-900 ring-1 ring-inset ring-blue-200'
          : 'bg-blue-50 text-blue-900 border-l-4 border-blue-600')
      : 'text-gray-700';

  const hover =
    active
      ? '' // active already highlighted
      : 'hover:bg-blue-50 hover:text-blue-700';

  return (
    <Link
      to={item.href}
      className={classNames(base, state, hover)}
      title={collapsed ? item.name : undefined}
      onClick={onClick}
    >
      <Icon
        className={classNames(
          active ? 'text-blue-700' : 'text-gray-400 group-hover:text-blue-600',
          collapsed ? 'h-6 w-6' : 'mr-3 h-6 w-6 flex-shrink-0'
        )}
      />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );
}

/* ---------------------------------------------
 * Fixed flyout (portal) used when sidebar is collapsed – themed
 * -------------------------------------------*/
function FlyoutPortal({ open, anchorRect, title, items, onMouseEnter, onMouseLeave, onItemClick }) {
  const node = document.body;
  if (!open || !anchorRect) return null;

  const style = {
    position: 'fixed',
    top: Math.round(anchorRect.top),
    left: Math.round(anchorRect.right + 8), // 8px gap
    zIndex: 9999,
  };

  return createPortal(
    <div
      style={style}
      className="min-w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="text-xs font-semibold text-slate-600 px-2 py-1 flex items-center">
        <span className="mr-2 inline-block h-2 w-2 rounded-full bg-orange-500/70" />
        {title}
      </div>
      {items.map((it) => (
        <Link
          key={it.name}
          to={it.href}
          className="flex items-center px-2 py-2 text-sm font-medium rounded-md text-slate-700 hover:bg-blue-50 hover:text-blue-700"
          onClick={onItemClick}
        >
          <it.icon className="mr-3 h-5 w-5 text-slate-400" />
          <span>{it.name}</span>
        </Link>
      ))}
    </div>,
    node
  );
}

/* ---------------------------------------------
 * Collapsible group
 *  - Expanded: inline accordion (click to toggle)
 *  - Collapsed: fixed portal flyout (hover with hover-lock)
 * -------------------------------------------*/
function CollapsibleGroup({ title, icon: TitleIcon, items, collapsed, storageKey, onItemClick }) {
  const location = useLocation();

  // Prefer persisted value; else open if a child route is active
  const computeRouteOpen = () =>
    items.some((it) => location.pathname === it.href || location.pathname.startsWith(it.href + '/'));

  const [open, setOpen] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved != null) {
      setOpen(saved === 'true');
    } else {
      setOpen(computeRouteOpen());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, String(open));
  }, [open, storageKey]);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved == null) setOpen(computeRouteOpen());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Collapsed flyout state/position (hover-lock)
  const headerRef = useRef(null);
  const [flyOpen, setFlyOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setFlyOpen(false), 250);
  };

  const openFly = () => {
    if (!headerRef.current) return;
    setRect(headerRef.current.getBoundingClientRect());
    setFlyOpen(true);
  };

  const onHeaderEnter = () => {
    cancelClose();
    openFly();
  };
  const onHeaderLeave = () => {
    scheduleClose();
  };

  useEffect(() => {
    if (!flyOpen) return;
    const refresh = () => {
      if (!headerRef.current) return setFlyOpen(false);
      setRect(headerRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', refresh, true);
    window.addEventListener('resize', refresh);
    return () => {
      window.removeEventListener('scroll', refresh, true);
      window.removeEventListener('resize', refresh);
    };
  }, [flyOpen]);

  // Themed header (de-emphasized, orange accent icon, subtle separators)
  return (
    <div className={classNames('relative', collapsed ? 'px-1' : 'px-2', 'mt-2 pt-2 border-t border-slate-200')}> 
      <button
        type="button"
        ref={headerRef}
        className={classNames(
          'text-slate-700',
          collapsed
            ? 'w-12 h-12 mx-auto flex items-center justify-center rounded-xl hover:bg-slate-100'
            : 'flex w-full items-center px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded-md hover:bg-slate-100'
        )}
        onClick={() => (!collapsed ? setOpen((v) => !v) : null)}
        onMouseEnter={collapsed ? onHeaderEnter : undefined}
        onMouseLeave={collapsed ? onHeaderLeave : undefined}
        title={collapsed ? title : undefined}
      >
        <TitleIcon
          className={classNames('flex-shrink-0', collapsed ? 'h-5 w-5 text-orange-500' : 'h-4 w-4 mr-2 text-orange-500')}
        />
        {!collapsed && <span className="text-slate-700">{title}</span>}
        {!collapsed && (
          <ChevronDownIcon
            className={classNames(
              'ml-auto h-4 w-4 text-slate-400 transition-transform',
              open ? 'rotate-180' : ''
            )}
          />
        )}
      </button>

      {/* Expanded → inline list */}
      {!collapsed && open && (
        <nav className="mt-1 space-y-1">
          {items.map((it) => {
            const active = location.pathname === it.href || location.pathname.startsWith(it.href + '/');
            return (
              <LinkRow
                key={it.name}
                item={it}
                active={active}
                collapsed={false}
                onClick={onItemClick}
              />
            );
          })}
        </nav>
      )}

      {/* Collapsed → portal flyout */}
      {collapsed && (
        <FlyoutPortal
          open={flyOpen}
          anchorRect={rect}
          title={title}
          items={items}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onItemClick={onItemClick}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------
 * Simple list (User Workspace) – themed
 * -------------------------------------------*/
function SimpleList({ title, icon: TitleIcon, items, collapsed, onItemClick }) {
  const location = useLocation();
  return (
    <div className={classNames(collapsed ? 'px-1' : 'px-2', 'mt-2 pt-2 border-t border-slate-200')}> 
      <div
        className={classNames(
          'flex items-center',
          collapsed
            ? 'w-12 h-12 mx-auto justify-center rounded-xl text-orange-500'
            : 'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700'
        )}
        title={collapsed ? title : undefined}
      >
        <TitleIcon className={classNames(collapsed ? 'h-5 w-5' : 'h-4 w-4 mr-2 text-orange-500')} />
        {!collapsed && <span>{title}</span>}
      </div>

      <nav className="mt-1 space-y-1">
        {items.map((it) => {
          const active =
            location.pathname === it.href || location.pathname.startsWith(it.href + '/');
          return (
            <LinkRow
              key={it.name}
              item={it}
              active={active}
              collapsed={collapsed}
              onClick={onItemClick}
            />
          );
        })}
      </nav>
    </div>
  );
}

/* ---------------------------------------------
 * Sidebar wrapper (mobile overlay + desktop collapsible) – themed
 * -------------------------------------------*/
export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  // Persist collapsed state for desktop
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem('rism.sidebar.collapsed');
    if (saved != null) setCollapsed(saved === 'true');
  }, []);
  useEffect(() => {
    localStorage.setItem('rism.sidebar.collapsed', String(collapsed));
  }, [collapsed]);

  return (
    <>
      {/* Mobile sidebar (overlay) */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}
        aria-modal="true"
        role="dialog"
      >
        <div className="fixed inset-0 bg-gray-900/40" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-gray-50 shadow-xl">
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 bg-white/80 backdrop-blur">
            <img src="/media/ExcelAcademyLogo.png" alt="Excel Academy Logo" className="h-8 w-auto" />
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-2 flex-1 overflow-y-auto">
            <SimpleList
              title="User Workspace"
              icon={HomeIcon}
              items={userWorkspace}
              collapsed={false}
              onItemClick={() => setSidebarOpen(false)}
            />
            <CollapsibleGroup
              title="Admin Workspace"
              icon={Cog6ToothIcon}
              items={adminWorkspace}
              collapsed={false}
              storageKey="rism.sidebar.adminWorkspaceOpen"
              onItemClick={() => setSidebarOpen(false)}
            />
            <CollapsibleGroup
              title="Reports"
              icon={Cog6ToothIcon}
              items={reports}
              collapsed={false}
              storageKey="rism.sidebar.reportsOpen"
              onItemClick={() => setSidebarOpen(false)}
            />
            <CollapsibleGroup
              title="Settings"
              icon={Cog6ToothIcon}
              items={settingsItems}
              collapsed={false}
              storageKey="rism.sidebar.settingsOpen"
              onItemClick={() => setSidebarOpen(false)}
            />
            <CollapsibleGroup
              title="Administration"
              icon={ShieldCheckIcon}
              items={administrationItems}
              collapsed={false}
              storageKey="rism.sidebar.administrationOpen"
              onItemClick={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      </div>

      {/* Desktop sidebar (collapsible) */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div
          className={classNames(
            'flex flex-col transition-all duration-200 border-r border-slate-200 bg-gray-50 shadow-sm',
            collapsed ? 'w-20' : 'w-64'
          )}
        >
          <div className={classNames('flex items-center justify-between px-4 pt-5 pb-4 bg-white/70 backdrop-blur border-b border-slate-200')}>
            <div className="flex items-center gap-2">
              <img
                src="/media/ExcelAcademyLogo.png"
                alt="Excel Academy Logo"
                className={classNames('h-8 w-auto transition-all', collapsed && 'hidden')}
              />
            </div>
            <button
              type="button"
              className={classNames(
                'rounded-md p-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500',
                collapsed ? 'mx-auto' : ''
              )}
              onClick={() => setCollapsed((v) => !v)}
              aria-label="Toggle sidebar"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <Bars3Icon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
            </button>
          </div>

          {/* Sections */}
          <div className="mt-2 flex-1 overflow-y-auto">
            <SimpleList title="User Workspace" icon={HomeIcon} items={userWorkspace} collapsed={collapsed} />
            <SimpleList title="Admin Workspace" icon={Cog6ToothIcon} items={adminWorkspace} collapsed={collapsed} />
            <SimpleList title="Reports" icon={Cog6ToothIcon} items={reports} collapsed={collapsed} />
            <CollapsibleGroup
              title="Settings"
              icon={Cog6ToothIcon}
              items={settingsItems}
              collapsed={collapsed}
              storageKey="rism.sidebar.settingsOpen"
            />
            <CollapsibleGroup
              title="Administration"
              icon={ShieldCheckIcon}
              items={administrationItems}
              collapsed={collapsed}
              storageKey="rism.sidebar.administrationOpen"
            />
          </div>
        </div>
        <div><p></p></div>
      </div>
    </>
  );
}
